import { useState } from "react";
import { clsx } from "clsx";
import {
  Bot, Download, FileImage, FileSignature, FileText, FileType2, History, Pencil, Trash2,
} from "lucide-react";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { pdfErrorText, useDownloadDocument } from "../api/pdfApi";
import type { PdfDocument } from "../types";

const KIND_ICON = { pdf: FileText, image: FileImage, office: FileType2, other: FileText };

export function humanSize(bytes: number): string {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Pasek plików sprawy. Zaznaczenie jest wielokrotne (Ctrl/Shift jak w menedżerze
 * plików), bo narzędzia typu „scal” albo „porównaj” pracują na kilku plikach naraz.
 */
export function DocumentStrip({
  documents,
  selectedIds,
  onSelect,
  onUpload,
  onRename,
  onDelete,
  onHistory,
  onError,
  uploading,
}: {
  documents: PdfDocument[];
  selectedIds: string[];
  onSelect: (ids: string[]) => void;
  onUpload: (files: File[]) => void;
  onRename: (doc: PdfDocument, name: string) => void;
  onDelete: (doc: PdfDocument) => void;
  onHistory: (doc: PdfDocument) => void;
  onError: (message: string) => void;
  uploading: boolean;
}) {
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const download = useDownloadDocument();

  const click = (doc: PdfDocument, e: React.MouseEvent) => {
    if (e.metaKey || e.ctrlKey) {
      onSelect(
        selectedIds.includes(doc.id)
          ? selectedIds.filter((id) => id !== doc.id)
          : [...selectedIds, doc.id],
      );
      return;
    }
    if (e.shiftKey && selectedIds.length > 0) {
      const ids = documents.map((d) => d.id);
      const from = ids.indexOf(selectedIds[selectedIds.length - 1]);
      const to = ids.indexOf(doc.id);
      const [a, b] = from < to ? [from, to] : [to, from];
      onSelect(ids.slice(a, b + 1));
      return;
    }
    onSelect([doc.id]);
  };

  return (
    <div className="flex items-stretch gap-2 overflow-x-auto border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-2">
      {documents.map((doc) => {
        const Icon = KIND_ICON[doc.kind] ?? FileText;
        const active = selectedIds.includes(doc.id);
        const order = selectedIds.indexOf(doc.id) + 1;
        return (
          <div
            key={doc.id}
            onClick={(e) => click(doc, e)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && onSelect([doc.id])}
            aria-pressed={active}
            className={clsx(
              "group relative flex w-[190px] shrink-0 cursor-pointer flex-col justify-between border px-2.5 py-2 transition-colors duration-100",
              active
                ? "border-[var(--color-primary)] bg-[var(--color-bg-surface)]"
                : "border-[var(--color-border)] bg-[var(--color-bg-surface)] hover:border-[var(--color-border-strong)]",
            )}
          >
            {active && selectedIds.length > 1 && (
              <span className="absolute -left-px -top-px flex h-4 w-4 items-center justify-center bg-[var(--color-primary)] font-mono text-[10px] font-bold text-white">
                {order}
              </span>
            )}
            <div className="flex items-start gap-2">
              <Icon
                size={16}
                className={clsx(
                  "mt-0.5 shrink-0",
                  active ? "text-[var(--color-primary)]" : "text-[var(--color-text-muted)]",
                )}
              />
              <div className="min-w-0 flex-1">
                {editing === doc.id ? (
                  <input
                    autoFocus
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onBlur={() => {
                      if (draft.trim() && draft !== doc.name) onRename(doc, draft.trim());
                      setEditing(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") e.currentTarget.blur();
                      if (e.key === "Escape") setEditing(null);
                    }}
                    className="input-base px-1 py-0.5 text-[11px]"
                  />
                ) : (
                  <p
                    className="truncate text-[12px] font-medium leading-tight text-[var(--color-text-primary)]"
                    title={doc.name}
                  >
                    {doc.name}
                  </p>
                )}
                <p className="mt-0.5 truncate font-mono text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">
                  {doc.page_count ? `${doc.page_count} str.` : doc.kind}
                  {" · "}
                  {humanSize(doc.size_bytes)}
                  {doc.version_number > 1 && ` · w${doc.version_number}`}
                  {doc.has_signature && (
                    <span
                      className="ml-1 inline-flex items-center gap-0.5 text-[var(--color-primary)]"
                      title="Podpis elektroniczny — każda zmiana pliku go unieważni"
                    >
                      · <FileSignature size={10} /> podpis
                    </span>
                  )}
                </p>
              </div>
            </div>

            <div className="mt-1.5 flex items-center justify-between">
              <span className="flex items-center gap-1 truncate text-[10px] text-[var(--color-text-muted)]">
                {doc.last_source === "ai" && (
                  <Bot size={11} className="shrink-0 text-[var(--color-primary)]" />
                )}
                <span className="truncate" title={doc.last_note}>
                  {doc.last_note || "wgrany"}
                </span>
              </span>
              {/* Ikony są WIDOCZNE od razu i mają realne pole kliknięcia: przy
                  ukrytych 12-pikselowych celach trafienie w kosz było loterią. */}
              <span className="-mr-1 flex shrink-0 items-center">
                <button
                  type="button"
                  title="Zmień nazwę"
                  aria-label={`Zmień nazwę ${doc.name}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setDraft(doc.name);
                    setEditing(doc.id);
                  }}
                  className="p-1 text-[var(--color-text-muted)] transition-colors hover:text-[var(--color-primary)]"
                >
                  <Pencil size={13} />
                </button>
                <button
                  type="button"
                  title="Historia wersji"
                  aria-label={`Historia ${doc.name}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onHistory(doc);
                  }}
                  className="p-1 text-[var(--color-text-muted)] transition-colors hover:text-[var(--color-primary)]"
                >
                  <History size={13} />
                </button>
                <button
                  type="button"
                  title="Pobierz"
                  aria-label={`Pobierz ${doc.name}`}
                  disabled={download.isPending}
                  onClick={(e) => {
                    e.stopPropagation();
                    download.mutate(doc, {
                      onError: (err) =>
                        onError(pdfErrorText(err, "Nie udało się pobrać pliku.")),
                    });
                  }}
                  className="p-1 text-[var(--color-text-muted)] transition-colors hover:text-[var(--color-primary)] disabled:opacity-40"
                >
                  <Download size={13} />
                </button>
                <button
                  type="button"
                  title="Usuń z pulpitu"
                  aria-label={`Usuń ${doc.name}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(doc);
                  }}
                  className="p-1 text-[var(--color-text-muted)] transition-colors hover:text-[var(--color-primary)]"
                >
                  <Trash2 size={13} />
                </button>
              </span>
            </div>
          </div>
        );
      })}

      <FileDropZone
        multiple
        disabled={uploading}
        onSelect={onUpload}
        label={uploading ? "Wgrywam…" : "Upuść pliki"}
        className="w-[190px] shrink-0"
      />
    </div>
  );
}
